import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiDownload2Line, RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { isIOS } from './platform';
import { canPromptInstall, promptInstall, subscribeInstall, trackInstall } from './install';
import styles from './InstallSheet.module.css';

// Install tutorial. Apple offers no install API (WebKit's position on the Web
// Install API is "oppose"), so on iPhone/iPad the sheet walks the user
// through the Share sheet one tap per screen, with a mockup of what the phone
// shows and the button to press highlighted. Chrome on Android/desktop gets
// one Install button that opens the native dialog.

const isIPad = () => /iPad/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Browsers embedded in other apps (Instagram, Facebook, Gmail, TikTok, the
// Google app...) have no "Add to Home Screen". Chrome/Firefox on iOS do have
// it, but Safari is the simpler path, so they get the same first step.
const inAppBrowser = () => {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|TikTok|musical_ly|Snapchat|GSA\/|LinkedInApp|Pinterest|Gmail|CriOS|FxiOS|EdgiOS|DuckDuckGo|Brave/i.test(ua);
};

const SITE_URL = 'https://byopdf.com';

/* ---------- mockups (inline SVG: render offline, follow the theme) ---------- */

const Highlight = ({ x, y, w, h, r = 12 }) => (
  <rect className={styles.pulse} x={x - 6} y={y - 6} width={w + 12} height={h + 12} rx={r + 6} fill="none" stroke="var(--primary-color)" strokeWidth="3" />
);

// Safari's share glyph: a box with an arrow rising out of it.
const ShareGlyph = ({ x, y, s = 22, color = 'currentColor' }) => (
  <g transform={`translate(${x} ${y})`} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={`M${s * 0.3} ${s * 0.4} H${s * 0.18} V${s} H${s * 0.82} V${s * 0.4} H${s * 0.7}`} />
    <path d={`M${s / 2} ${s * 0.62} V0 M${s * 0.3} ${s * 0.2} L${s / 2} 0 L${s * 0.7} ${s * 0.2}`} />
  </g>
);

// Step: Safari with the Share button in the bottom bar (iPhone) or top right (iPad).
const SafariMock = ({ ipad }) => (
  <svg viewBox="0 0 320 200" className={styles.mock} aria-hidden="true">
    <rect x="0.5" y="0.5" width="319" height="199" rx="18" fill="var(--surface-color)" stroke="var(--border-color-strong)" />
    {ipad ? (
      <>
        <rect x="16" y="14" width="288" height="32" rx="10" fill="var(--background-secondary)" />
        <text x="40" y="35" fontSize="13" fill="var(--text-secondary)">byopdf.com</text>
        <ShareGlyph x="268" y="19" s={22} color="var(--primary-color)" />
        <Highlight x={262} y={14} w={34} h={32} r={10} />
        <rect x="16" y="60" width="288" height="120" rx="10" fill="var(--background-secondary)" opacity="0.6" />
      </>
    ) : (
      <>
        <rect x="18" y="18" width="284" height="110" rx="10" fill="var(--background-secondary)" />
        <text x="32" y="48" fontSize="13" fill="var(--text-secondary)">Your library</text>
        <rect x="32" y="60" width="60" height="50" rx="6" fill="var(--border-color-strong)" />
        <rect x="100" y="60" width="60" height="50" rx="6" fill="var(--border-color-strong)" />
        <rect x="168" y="60" width="60" height="50" rx="6" fill="var(--border-color-strong)" />
        <rect x="18" y="140" width="284" height="44" rx="14" fill="var(--background-secondary)" />
        <text x="40" y="167" fontSize="16" fill="var(--text-secondary)">‹</text>
        <text x="72" y="167" fontSize="16" fill="var(--text-secondary)">›</text>
        <rect x="140" y="146" width="40" height="32" rx="8" fill="var(--surface-color)" />
        <ShareGlyph x="149" y="151" s={22} color="var(--primary-color)" />
        <Highlight x={140} y={146} w={40} h={32} r={8} />
        <rect x="232" y="153" width="18" height="18" rx="3" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" />
        <rect x="266" y="153" width="18" height="18" rx="3" fill="none" stroke="var(--text-secondary)" strokeWidth="1.5" />
      </>
    )}
  </svg>
);

// Step: the Share sheet with "Add to Home Screen" highlighted.
const ShareSheetMock = () => (
  <svg viewBox="0 0 320 200" className={styles.mock} aria-hidden="true">
    <rect x="0.5" y="0.5" width="319" height="199" rx="18" fill="var(--surface-color)" stroke="var(--border-color-strong)" />
    <rect x="130" y="8" width="60" height="5" rx="2.5" fill="var(--border-color-strong)" />
    {[['Copy', 24], ['Add to Reading List', 58], ['Add Bookmark', 92], ['Add to Home Screen', 126], ['Markup', 160]].map(([label, y], i) => (
      <g key={label}>
        <rect x="16" y={y} width="288" height="30" rx="8" fill={i === 3 ? 'var(--primary-color-10)' : 'transparent'} />
        <text x="28" y={y + 20} fontSize="13" fill={i === 3 ? 'var(--text-primary)' : 'var(--text-secondary)'} fontWeight={i === 3 ? 600 : 400}>{label}</text>
        {i === 3 ? (
          <g transform="translate(276 133)" fill="none" stroke="var(--primary-color)" strokeWidth="2" strokeLinecap="round">
            <rect x="0" y="0" width="16" height="16" rx="4" />
            <path d="M8 4v8M4 8h8" />
          </g>
        ) : (
          <circle cx="284" cy={y + 15} r="6" fill="var(--border-color-strong)" />
        )}
      </g>
    ))}
    <Highlight x={16} y={126} w={288} h={30} r={8} />
  </svg>
);

// Step: the Add dialog with the Add button highlighted.
const AddDialogMock = () => (
  <svg viewBox="0 0 320 200" className={styles.mock} aria-hidden="true">
    <rect x="0.5" y="0.5" width="319" height="199" rx="18" fill="var(--surface-color)" stroke="var(--border-color-strong)" />
    <text x="24" y="34" fontSize="14" fill="var(--text-secondary)">Cancel</text>
    <text x="160" y="34" fontSize="14" textAnchor="middle" fontWeight="600" fill="var(--text-primary)">Add to Home Screen</text>
    <rect x="254" y="16" width="44" height="26" rx="8" fill="var(--primary-color-10)" />
    <text x="276" y="34" fontSize="14" textAnchor="middle" fontWeight="700" fill="var(--primary-color)">Add</text>
    <Highlight x={254} y={16} w={44} h={26} r={8} />
    <rect x="24" y="66" width="272" height="64" rx="12" fill="var(--background-secondary)" />
    <image href="/icons/icon-192.png" x="36" y="76" width="44" height="44" />
    <text x="96" y="94" fontSize="13" fontWeight="600" fill="var(--text-primary)">BYOPDF</text>
    <text x="96" y="116" fontSize="11" fill="var(--text-secondary)">byopdf.com</text>
    <text x="24" y="160" fontSize="12" fill="var(--text-secondary)">An icon will be added to your Home Screen</text>
    <text x="24" y="176" fontSize="12" fill="var(--text-secondary)">so you can quickly access this website.</text>
  </svg>
);

// Step: the icon on the Home Screen.
const HomeScreenMock = () => (
  <svg viewBox="0 0 320 200" className={styles.mock} aria-hidden="true">
    <rect x="0.5" y="0.5" width="319" height="199" rx="18" fill="var(--background-secondary)" stroke="var(--border-color-strong)" />
    {[0, 1, 2, 3].map((col) => [0, 1].map((row) => (
      (col === 1 && row === 0) ? null : (
        <rect key={`${col}-${row}`} x={30 + col * 70} y={26 + row * 80} width="50" height="50" rx="12" fill="var(--border-color-strong)" opacity="0.6" />
      )
    )))}
    <image href="/icons/icon-192.png" x="100" y="26" width="50" height="50" />
    <text x="125" y="92" fontSize="11" textAnchor="middle" fill="var(--text-primary)">BYOPDF</text>
    <Highlight x={100} y={26} w={50} h={50} r={12} />
  </svg>
);

// Step 0 (browsers embedded in other apps): open in Safari.
const SafariAppMock = () => (
  <svg viewBox="0 0 320 200" className={styles.mock} aria-hidden="true">
    <rect x="0.5" y="0.5" width="319" height="199" rx="18" fill="var(--surface-color)" stroke="var(--border-color-strong)" />
    <circle cx="160" cy="88" r="44" fill="var(--primary-color-10)" stroke="var(--primary-color)" strokeWidth="2" />
    <circle cx="160" cy="88" r="30" fill="none" stroke="var(--primary-color)" strokeWidth="2" strokeDasharray="3 5" />
    <path d="M160 58 L170 86 L160 118 L150 86 Z" fill="var(--primary-color)" />
    <text x="160" y="160" fontSize="13" textAnchor="middle" fill="var(--text-secondary)">Safari</text>
  </svg>
);

/* ---------- the sheet ---------- */

export default function InstallSheet({ onClose, reason = 'download' }) {
  const [promptable, setPromptable] = useState(canPromptInstall());
  const [step, setStep] = useState(0);
  const [copied, setCopied] = useState(false);
  const ios = isIOS();
  const ipad = ios && isIPad();
  const embedded = ios && inAppBrowser();

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const unsubscribe = subscribeInstall(() => setPromptable(canPromptInstall()));
    trackInstall('pwa_install_sheet_open', { reason, platform: ios ? (ipad ? 'ipad' : 'iphone') : 'other', embedded });
    return () => { window.removeEventListener('keydown', onKey); unsubscribe(); };
  }, [onClose, reason, ios, ipad, embedded]);

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') onClose();
  };

  const copyLink = useCallback(async () => {
    try { await navigator.clipboard.writeText(SITE_URL); setCopied(true); } catch (e) { setCopied(false); }
  }, []);

  const shareBar = ipad ? 'at the top right of Safari' : 'in the bar at the bottom of Safari';

  const iosSteps = [
    ...(embedded ? [{
      title: 'First, open this page in Safari',
      body: 'Downloads live inside the BYOPDF app on your Home Screen. The browser inside this app cannot add it there; Safari can.',
      mock: <SafariAppMock />,
      action: (
        <div className={styles.actions}>
          <a className={styles.primary} href={`x-safari-${SITE_URL}`}>Open in Safari</a>
          <button className={styles.secondary} onClick={copyLink}>{copied ? 'Link copied. Paste it in Safari' : 'Or copy the link'}</button>
        </div>
      )
    }] : []),
    {
      title: 'Tap the Share button',
      body: `It is the square with an arrow pointing up, ${shareBar}.`,
      mock: <SafariMock ipad={ipad} />
    },
    {
      title: 'Choose “Add to Home Screen”',
      body: 'Scroll down the list a little if you do not see it straight away.',
      mock: <ShareSheetMock />
    },
    {
      title: 'Tap “Add”',
      body: 'Top right. You can keep the name BYOPDF.',
      mock: <AddDialogMock />
    },
    {
      title: 'Open BYOPDF from your Home Screen',
      body: 'Sign in once, then tap Download on any book. It stays on your phone and plays with no signal and the screen locked.',
      mock: <HomeScreenMock />
    }
  ];

  const total = iosSteps.length;
  const current = iosSteps[Math.min(step, total - 1)];
  const last = step >= total - 1;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="install-title" onClick={(e) => e.stopPropagation()}>
        <button className={styles.close} onClick={onClose} aria-label="Close"><RiCloseLine size={20} /></button>

        {ios ? (
          <>
            <div className={styles.stepHeader}>
              <p className={styles.kicker}>{reason === 'push' ? 'To get notified when your books are ready:' : 'To download audiobooks into the app and listen offline:'}</p>
              <span className={styles.stepCount}>Step {step + 1} of {total}</span>
              <h2 id="install-title" className={styles.title}>{current.title}</h2>
              <p className={styles.lead}>{current.body}</p>
            </div>
            <div className={styles.mockWrap}>{current.mock}</div>
            {current.action}
            <div className={styles.nav}>
              <button className={styles.navBtn} onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} aria-label="Previous step">
                <RiArrowLeftSLine size={22} />
              </button>
              <div className={styles.dots} aria-hidden="true">
                {iosSteps.map((_, i) => <span key={i} className={i === step ? styles.dotActive : styles.dot} />)}
              </div>
              {last ? (
                <button className={styles.navNext} onClick={onClose}>Done</button>
              ) : (
                <button className={styles.navNext} onClick={() => setStep((s) => s + 1)}>
                  Next <RiArrowRightSLine size={20} />
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.iconWrap}>
              <img src="/icons/icon-192.png" alt="" width={56} height={56} />
            </div>
            <h2 id="install-title" className={styles.title}>Install BYOPDF</h2>
            <p className={styles.lead}>
              {promptable
                ? 'Install it to download audiobooks to this device and listen offline. One tap, no app store.'
                : 'Open the browser menu (⋮) or the install icon in the address bar, then choose Install app or Add to Home screen.'}
            </p>
            {promptable ? (
              <button className={styles.primary} onClick={handleInstall}>
                <RiDownload2Line size={18} style={{ verticalAlign: '-3px', marginRight: 8 }} />
                Install BYOPDF
              </button>
            ) : (
              <button className={styles.primary} onClick={onClose}>Got it</button>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
